import { groupBy, minBy } from 'es-toolkit';

import { CalculatedLine, Component } from '@paragon/contract-library';
import { calculateLineGeneration } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch } from '@paragon/utilities';

/**
 * When multiple reference lines are coincident, one must be chosen (deterministically) to be the "blessed" line which
 *   will take precedence. This affects both reference line visuals (render order) and raycasting (which line in the
 *   stack will be hit for mouse move and click events).
 */
export function pickBlessedCoincidentLine(
  possibilities: CalculatedLine[],
  dependencies: { component: Component; selectedReferenceLineGuid: string | null },
): CalculatedLine {
  // The goal is to get down to a single line. Start with a list of all the possibilities, and narrow based on selected
  //   status, then render mode, then generation. At each step, return early if possible.

  const selectedReferenceLine = possibilities.find((line) => line.guid === dependencies.selectedReferenceLineGuid);
  if (selectedReferenceLine) return selectedReferenceLine;

  possibilities = groupAndTakeHighestPriority(possibilities, getLineTypePriority, PRIORITIES);
  if (possibilities.length === 1) return possibilities[0];

  const oldestLine = minBy(possibilities, (line) => calculateLineGeneration(line, dependencies.component))!;
  return oldestLine;
}

/**
 * Takes a list of values, groups them using the given iteratee, and returns the group with the highest priority.
 *
 * @param possibilities - The list of values to group.
 * @param iteratee - A function that returns the key to group by.
 * @param priorities - The list of keys to prioritize, in descending order of desirability.
 */
function groupAndTakeHighestPriority<TKey extends string | number, TValue>(
  possibilities: TValue[],
  iteratee: (value: TValue) => TKey,
  priorities: TKey[],
): TValue[] {
  const groups = groupBy(possibilities, iteratee);

  for (const priority of priorities) {
    if (priority in groups) {
      return groups[priority];
    }
  }

  return [];
}

enum Priority {
  High,
  Medium,
  Low,
}

const PRIORITIES = [Priority.High, Priority.Medium, Priority.Low];

function getLineTypePriority(line: CalculatedLine): Priority {
  switch (line.lineType) {
    case 'Absolute':
      return Priority.High;

    case 'Parallel':
    case 'PointSlope':
    case 'PointToPoint':
    case 'Perpendicular':
    case 'Division':
      return Priority.Medium;

    case 'Member':
      return Priority.Low;

    default:
      assertExhaustiveSwitch(line, 'Unrecognized line type.');
  }
}
